import { ApplicantType } from "shared/types";
import { WarningRuleLevels } from "api/landlord/warnings-report/types";
import {
  Template,
  VerificationSchemas
} from "types/landlord/verificationTemplate";

export interface PremiumWarningsReportSettings {
  warning_rules?: WarningRuleLevels;
}

// The editor's working state for the premium add-on, keyed by role. The draft —
// not template.verification_schemas — is the single source of truth while
// editing: the key is extracted into it on load and re-applied on validate/save.
export type PremiumWarningsDraft = Partial<
  Record<ApplicantType, PremiumWarningsReportSettings>
>;

type RoleSchemas = VerificationSchemas[ApplicantType.Applicant];

const ROLES = [
  ApplicantType.Applicant,
  ApplicantType.CoSigner,
  ApplicantType.Occupant
] as const;

// Pulls the saved premium settings out of fetched verification schemas so the
// editor's toggle and Configure page reflect them, leaving schemas without the
// key — the draft is re-applied at validate/save time.
export const extractPremiumWarningsDraft = (
  schemas: VerificationSchemas
): {
  draft: PremiumWarningsDraft;
  schemasWithoutPremium: VerificationSchemas;
} => {
  const draft: PremiumWarningsDraft = {};
  const schemasWithoutPremium = { ...schemas };

  ROLES.forEach((role) => {
    const roleSchemas: RoleSchemas | undefined = schemas[role];
    if (!roleSchemas?.premium_warnings_report) return;

    const { premium_warnings_report: premium, ...rest } = roleSchemas;
    draft[role] = { warning_rules: premium.warning_rules ?? {} };
    schemasWithoutPremium[role] = rest;
  });

  return { draft, schemasWithoutPremium };
};

// Folds the draft back into the template for validate / create / update: roles
// with a draft entry get the premium key (and their configured levels), roles
// without one carry no key — which is how the API models premium being off.
export const applyPremiumWarningsDraft = (
  template: Template,
  draft: PremiumWarningsDraft
): Template => {
  const verification_schemas = { ...template.verification_schemas };

  ROLES.forEach((role) => {
    const roleSchemas = verification_schemas[role];
    if (!roleSchemas) return;

    const { premium_warnings_report: _stale, ...rest } = roleSchemas;
    const entry = draft[role];

    verification_schemas[role] = entry
      ? {
          ...rest,
          premium_warnings_report: { warning_rules: entry.warning_rules ?? {} }
        }
      : rest;
  });

  return { ...template, verification_schemas };
};
